import { Temporal } from '@js-temporal/polyfill'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'

import type { Template, TemplateCampaign } from '../types'

/**
 * Checks if a template should be hidden because its campaign hasn't started yet.
 * Returns true (hide) if:
 * - Template has linked campaigns AND
 * - The oldest linked campaign's startDate is still in the future
 *
 * Templates without campaigns are always visible (returns false).
 */
export const isTemplateStartDateInFuture = (
  template: Template,
  campaigns: TemplateCampaign[],
  currentTime: Temporal.ZonedDateTime = Temporal.Now.zonedDateTimeISO(
    CURRENT_TIME_ZONE,
  ),
): boolean => {
  // Templates without campaigns are always visible
  if (!template.linkedCampaigns || template.linkedCampaigns.length === 0) {
    return false
  }

  // Find all linked campaigns
  const linkedCampaigns = campaigns.filter((c) =>
    template.linkedCampaigns.includes(c.id),
  )

  if (linkedCampaigns.length === 0) {
    return false
  }

  // Find the oldest campaign startDate (earliest start)
  const oldestStartDate = linkedCampaigns.reduce<Temporal.ZonedDateTime | null>(
    (oldest, campaign) => {
      if (!oldest) return campaign.startDate
      return Temporal.ZonedDateTime.compare(campaign.startDate, oldest) < 0
        ? campaign.startDate
        : oldest
    },
    null,
  )

  if (!oldestStartDate) {
    return false
  }

  // Hide if the oldest campaign hasn't started yet
  return Temporal.ZonedDateTime.compare(oldestStartDate, currentTime) > 0
}
